/**
 * Who the signed-in account is, in their own words.
 *
 * A kind 0 profile is public and lives on public relays, not on the mail relay:
 * the mail relay is members-only and holds gift wraps, so asking it for a
 * profile returns nothing. That is why this talks to its own set of relays and
 * does not reuse the authenticated `RelayConnection`.
 *
 * Nothing here is trusted for anything but display. The name is the account's
 * own claim about itself, which is all a mail client needs it for.
 */
import { SimplePool, type Event as NostrEvent } from "nostr-tools";

export const KIND_METADATA = 0;

/**
 * Where profiles are looked up.
 *
 * purplepag.es is first on purpose: it indexes kind 0 and nothing else, so it
 * answers in one round trip where a general relay may not hold the event at all.
 */
export const PROFILE_RELAYS: string[] = (import.meta.env["VITE_PROFILE_RELAYS"] ??
  "wss://purplepag.es,wss://relay.damus.io,wss://nos.lol,wss://relay.primal.net")
  .split(",")
  .map((r: string) => r.trim())
  .filter(Boolean);

export interface Profile {
  /** Display name, already resolved from the several fields that carry one. */
  name: string;
  /** Avatar URL, http(s) only, or undefined when there is none worth showing. */
  picture?: string;
  nip05?: string;
}

/** The newest kind 0 across the relays, parsed. Null when nobody has one. */
export async function fetchProfile(
  pubkey: string,
  relays: string[] = PROFILE_RELAYS,
  maxWait = 4000,
): Promise<Profile | null> {
  const pool = new SimplePool();
  try {
    const events = await pool.querySync(relays, { kinds: [KIND_METADATA], authors: [pubkey] }, { maxWait });
    const newest = events.sort((a, b) => b.created_at - a.created_at)[0];
    return newest ? parseProfile(newest) : null;
  } finally {
    pool.close(relays);
  }
}

export function parseProfile(event: NostrEvent): Profile | null {
  let raw: Record<string, unknown>;
  try {
    raw = JSON.parse(event.content) as Record<string, unknown>;
  } catch {
    // A malformed profile is the author's problem, not an error to surface.
    return null;
  }
  const name = text(raw["display_name"]) || text(raw["name"]) || "";
  const profile: Profile = { name };
  const picture = text(raw["picture"]);
  if (picture && isDisplayableImage(picture)) profile.picture = picture;
  const nip05 = text(raw["nip05"]);
  if (nip05) profile.nip05 = nip05;
  return profile;
}

/**
 * The avatar's bytes, not its URL.
 *
 * The page's own CSP allows `img-src 'self' blob: data:` and nothing else, so a
 * remote `src` cannot load — which is the tracker rule enforced at the browser
 * rather than only in the renderer. The account's own picture is the one image
 * the art bible exempts, so it is fetched here and handed to the page as bytes:
 * the exemption costs one request, the rule stays airtight for every sender,
 * and a cached avatar costs no request at all.
 */
export async function fetchAvatar(url: string, maxBytes = 4 * 1024 * 1024): Promise<Uint8Array | null> {
  if (!isDisplayableImage(url)) return null;
  const response = await fetch(url, { referrerPolicy: "no-referrer", mode: "cors" });
  if (!response.ok) return null;
  // A profile can name any URL; only an image is worth putting in the bar.
  if (!(response.headers.get("content-type") ?? "").toLowerCase().startsWith("image/")) {
    return null;
  }
  const buffer = await response.arrayBuffer();
  if (buffer.byteLength > maxBytes) return null;
  return new Uint8Array(buffer);
}

/**
 * `data:` and `javascript:` are the two that matter here: the picture URL comes
 * off a relay, so it is treated as hostile input even though it is our own.
 */
export function isDisplayableImage(url: string): boolean {
  try {
    return /^https?:$/.test(new URL(url).protocol);
  } catch {
    return false;
  }
}

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}
